import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RatingStars } from '@/components/atoms/RatingStars';
import { ScoreBadge } from '@/components/atoms/ScoreBadge';
import { useModuleFilter } from '@/context/ModuleFilter';
import type { DailyModuleReport, DailyReportConsolidatedSummary } from '@/schemas/reports';
import { useDailyReports } from '@/services/Reports';

import { DATE_RANGES, type DateRange, DISTRIBUTION_KEYS, RANGE_DAYS } from './Reports.constants';
import { ReportsLoading } from './Reports.loading';
import { styles } from './Reports.styles';

const toISO = (d: Date): string => d.toISOString().slice(0, 10);

const computeRange = (range: DateRange): { from: string; to: string } => {
	const today = new Date();
	const to = toISO(today);
	const fromDate = new Date(today);

	fromDate.setUTCDate(fromDate.getUTCDate() - RANGE_DAYS[range]);
	return { from: toISO(fromDate), to };
};

const groupByModule = (items: DailyModuleReport[]): Map<string, DailyModuleReport[]> => {
	const map = new Map<string, DailyModuleReport[]>();

	items.forEach((r) => {
		const existing = map.get(r.moduleName) ?? [];

		existing.push(r);
		map.set(r.moduleName, existing);
	});

	return map;
};

const formatDay = (iso: string): string => {
	const d = new Date(`${iso}T00:00:00Z`);

	if (Number.isNaN(d.getTime())) return iso;
	return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatAverage = (avg: number | null | undefined): string => {
	if (avg === null || avg === undefined) return '—';
	return avg.toFixed(2);
};

const computeResolutionScore = (report: DailyModuleReport | undefined): number | null => {
	if (!report || report.conversationsTotal <= 0) return null;
	return report.closedTotal / report.conversationsTotal;
};

export const Reports = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'reports' });
	const { selectedModule } = useModuleFilter();
	const [range, setRange] = useState<DateRange>('7d');
	const [expandedReportKey, setExpandedReportKey] = useState<string | null>(null);
	const params = useMemo(
		() => ({ ...computeRange(range), moduleName: selectedModule, includeConsolidated: true }),
		[range, selectedModule],
	);
	const { data, isLoading, isError } = useDailyReports(params);

	if (isLoading) return <ReportsLoading />;
	if (isError || !data) {
		return (
			<main className={styles.content}>
				<p className="text-[13px] text-error">{t('ERROR_LOAD')}</p>
			</main>
		);
	}

	const grouped = groupByModule(data.items);
	const moduleNames = Array.from(grouped.keys());

	return (
		<>
			<header className={styles.topbar}>
				<nav className="flex items-center gap-3xs text-[12px] text-subtle">
					<span className="font-medium text-default">{t('BREADCRUMB')}</span>
				</nav>
				<div className="flex items-center gap-2xs">
					{DATE_RANGES.map((r) => (
						<button
							key={r}
							type="button"
							onClick={() => setRange(r)}
							className={`rounded-md px-xs py-3xs text-[12px] transition-colors ${
								r === range ? 'bg-purple-500 text-white' : 'bg-grey-25 text-default hover:bg-grey-50'
							}`}
						>
							{t(`RANGE_${r.toUpperCase()}`)}
						</button>
					))}
				</div>
			</header>

			<main className={styles.content}>
				<header>
					<p className={styles.pageEyebrow}>{t('EYEBROW')}</p>
					<h1 className={styles.pageH1}>{t('TITLE')}</h1>
					<p className={styles.pageSub}>{t('SUBTITLE')}</p>
				</header>

				{moduleNames.length === 0 ? (
					<section className="rounded-md border border-subtle bg-grey-25 px-xl py-xl">
						<p className="text-[14px] font-semibold text-default">{t('EMPTY_TITLE')}</p>
						<p className="mt-3xs text-[12px] text-subtle">{t('EMPTY_BODY')}</p>
					</section>
				) : (
					moduleNames.map((moduleName) => {
						const rows = grouped.get(moduleName) ?? [];
						const latest = rows[0];

						return (
							<section key={moduleName} className="flex flex-col gap-md">
								<h2 className="text-[16px] font-semibold capitalize text-default">{moduleName}</h2>

								<div className="grid grid-cols-1 gap-md md:grid-cols-3">
									<article className={styles.scoreCard}>
										<p className={styles.scoreLabel}>{t('LLM_SCORE_LABEL')}</p>
										<div className={styles.scoreValue}>
											<ScoreBadge score={latest?.llmScore ?? null} size="lg" />
										</div>
										<p className={styles.scoreSub}>
											{t('VERDICTS_SUMMARY', { passing: latest?.llmPassing ?? 0, total: latest?.llmTotal ?? 0 })}
										</p>
									</article>
									<article className={styles.scoreCard}>
										<p className={styles.scoreLabel}>{t('EXTERNAL_SCORE_LABEL')}</p>
										<div className={styles.scoreValue}>
											<span className="text-[36px] font-semibold tabular-nums">
												{formatAverage(latest?.externalAverage)}
											</span>
											{latest?.externalAverage ? <span className="text-[16px] text-subtle">/ 5</span> : null}
										</div>
										<p className={styles.scoreSub}>
											{t('RATINGS_SUMMARY', {
												count: latest?.externalTotal ?? 0,
												passing: latest?.externalPassing ?? 0,
											})}
										</p>
									</article>
									<article className={styles.scoreCard}>
										<p className={styles.scoreLabel}>{t('RESOLUTION_LABEL')}</p>
										<div className={styles.scoreValue}>
											<ScoreBadge score={computeResolutionScore(latest)} size="lg" />
										</div>
										<p className={styles.scoreSub}>
											{t('RESOLUTION_SUMMARY', {
												closed: latest?.closedTotal ?? 0,
												total: latest?.conversationsTotal ?? 0,
											})}
										</p>
										<p className={styles.scoreSub}>
											{t('HANDOFFS_SUMMARY', {
												count: latest?.handoffTotal ?? 0,
												total: latest?.conversationsTotal ?? 0,
											})}
										</p>
									</article>
								</div>

								{latest && latest.externalTotal > 0 ? (
									<section className="rounded-md border border-subtle bg-white px-md py-sm">
										<header className="mb-xs flex items-center justify-between">
											<p className="text-[12px] font-semibold text-default">{t('DISTRIBUTION_TITLE')}</p>
											<RatingStars value={Math.round(latest.externalAverage ?? 0)} size="sm" />
										</header>
										<div className="flex items-end gap-2xs">
											{DISTRIBUTION_KEYS.map((k) => {
												const count = latest.externalDistribution[k] ?? 0;
												const max = Math.max(1, ...Object.values(latest.externalDistribution));
												const heightPct = Math.max(4, Math.round((count / max) * 100));

												return (
													<div key={k} className="flex flex-1 flex-col items-center gap-3xs">
														<div className="flex h-[60px] w-full items-end">
															<div
																className="w-full rounded-t-sm bg-purple-500"
																style={{ height: `${heightPct}%` }}
																aria-hidden
															/>
														</div>
														<span className="text-[11px] tabular-nums text-subtle">{k}★</span>
														<span className="text-[10px] tabular-nums text-default">{count}</span>
													</div>
												);
											})}
										</div>
									</section>
								) : null}

								<section className="overflow-x-auto rounded-md border border-subtle bg-white">
									<table className="min-w-full text-left text-[13px]">
										<thead className="border-b border-subtle bg-grey-25 text-[11px] font-medium uppercase tracking-wider text-subtle">
											<tr>
												<th className="px-md py-xs">{t('COL_DAY')}</th>
												<th className="px-md py-xs">{t('COL_LLM_TOTAL')}</th>
												<th className="px-md py-xs">{t('COL_LLM_SCORE')}</th>
												<th className="px-md py-xs">{t('COL_EXTERNAL_TOTAL')}</th>
												<th className="px-md py-xs">{t('COL_EXTERNAL_SCORE')}</th>
												<th className="px-md py-xs">{t('COL_EXTERNAL_AVG')}</th>
												<th className="px-md py-xs">{t('COL_CLOSED')}</th>
												<th className="px-md py-xs">{t('COL_HANDOFF')}</th>
												<th className="px-md py-xs">{t('COL_CONSOLIDATED')}</th>
											</tr>
										</thead>
										<tbody>
											{rows.map((r) => {
												const reportKey = `${r.moduleName}-${r.day}`;
												const isExpanded = expandedReportKey === reportKey;
												const hasConsolidated = Boolean(r.consolidatedSummary);

												return (
													<Fragment key={reportKey}>
														<tr className={`border-b border-subtle/50 ${hasConsolidated ? '' : 'bg-grey-25/40'}`}>
															<td className="px-md py-xs text-[12px] font-medium text-default">{formatDay(r.day)}</td>
															<td className="px-md py-xs text-[12px] tabular-nums text-default">
																{r.llmPassing}/{r.llmTotal}
															</td>
															<td className="px-md py-xs">
																<ScoreBadge score={r.llmScore ?? null} size="sm" />
															</td>
															<td className="px-md py-xs text-[12px] tabular-nums text-default">
																{r.externalPassing}/{r.externalTotal}
															</td>
															<td className="px-md py-xs">
																<ScoreBadge score={r.externalScore ?? null} size="sm" />
															</td>
															<td className="px-md py-xs text-[12px] tabular-nums text-default">
																{formatAverage(r.externalAverage)}
															</td>
															<td className="px-md py-xs text-[12px] tabular-nums text-default">
																{r.closedTotal}/{r.conversationsTotal}
															</td>
															<td className="px-md py-xs text-[12px] tabular-nums text-default">
																{r.handoffTotal}/{r.conversationsTotal}
															</td>
															<td className="px-md py-xs">
																{hasConsolidated ? (
																	<button
																		type="button"
																		aria-expanded={isExpanded}
																		onClick={() => setExpandedReportKey(isExpanded ? null : reportKey)}
																		className="rounded-md border border-subtle bg-white px-xs py-3xs text-[12px] text-default transition-colors hover:bg-grey-25"
																	>
																		{isExpanded ? t('HIDE_CONSOLIDATED') : t('SHOW_CONSOLIDATED')}
																	</button>
																) : (
																	<span className="inline-flex rounded-md border border-subtle bg-grey-25 px-xs py-3xs text-[12px] text-subtle">
																		{t('NO_CONSOLIDATED')}
																	</span>
																)}
															</td>
														</tr>
														{isExpanded && r.consolidatedSummary ? (
															<tr className="border-b border-subtle/50 bg-grey-25/60">
																<td colSpan={9} className="px-md py-sm">
																	<ConsolidatedSummary summary={r.consolidatedSummary} />
																</td>
															</tr>
														) : null}
													</Fragment>
												);
											})}
										</tbody>
									</table>
								</section>
							</section>
						);
					})
				)}
			</main>
		</>
	);
};

const ConsolidatedSummary = ({ summary }: { summary: DailyReportConsolidatedSummary }) => {
	const { t } = useTranslation('translation', { keyPrefix: 'reports' });

	return (
		<section className="rounded-md border border-subtle bg-white px-lg py-md">
			<p className="text-[11px] font-medium uppercase tracking-wider text-subtle">{t('CONSOLIDATED_TITLE')}</p>
			<p className="mt-xs max-w-[78ch] text-[13px] leading-6 text-default">{summary.summary}</p>
			<div className="mt-md grid grid-cols-1 gap-md md:grid-cols-3">
				<ConsolidatedList title={t('CONSOLIDATED_WENT_WELL')} items={summary.wentWell} />
				<ConsolidatedList title={t('CONSOLIDATED_NEEDS_REVIEW')} items={summary.needsReview} />
				<ConsolidatedList title={t('CONSOLIDATED_ACTIONS')} items={summary.recommendedActions} />
			</div>
		</section>
	);
};

const ConsolidatedList = ({ title, items }: { title: string; items: string[] }) => {
	if (items.length === 0) return null;

	return (
		<div>
			<p className="text-[12px] font-semibold text-default">{title}</p>
			<ul className="mt-2xs list-disc space-y-2xs pl-md text-[12px] leading-5 text-subtle">
				{items.map((item) => (
					<li key={item}>{item}</li>
				))}
			</ul>
		</div>
	);
};
